export type Registrar = 'Aliyun' | 'Tencent' | 'Huawei' | 'Namecheap' | 'GoDaddy' | 'Cloudflare' | 'NameSilo' | 'Porkbun' | 'Spaceship' | 'Gandi';

export interface DomainPrice {
  id: string;
  tld: string;
  registrar: Registrar;
  registerPrice: number;
  renewPrice: number;
  transferPrice: number;
  currency: 'CNY' | 'USD';
  url: string;
  tags?: string[];
  valueScore?: number; // 0-100, calculated based on price and popularity
  priceHistory?: { date: string; price: number }[];
}

// Approximate exchange rate for sorting
const USD_TO_CNY = 7.25;

export const getNormalizedPrice = (price: number, currency: 'CNY' | 'USD') => {
  return currency === 'USD' ? price * USD_TO_CNY : price;
};

export const domainPrices: DomainPrice[] = [
  // .com
  { 
    id: 'com-spaceship', 
    tld: '.com', 
    registrar: 'Spaceship', 
    registerPrice: 9.06, 
    renewPrice: 9.84, 
    transferPrice: 9.06, 
    currency: 'USD', 
    url: 'https://www.spaceship.com/', 
    tags: ['Best Value'],
    valueScore: 98,
    priceHistory: [
      { date: '2024-01-01', price: 8.98 },
      { date: '2024-06-01', price: 9.06 },
      { date: '2024-12-01', price: 8.88 } // Lowest
    ]
  },
  { 
    id: 'com-porkbun', 
    tld: '.com', 
    registrar: 'Porkbun', 
    registerPrice: 7.49, 
    renewPrice: 10.37, 
    transferPrice: 10.37, 
    currency: 'USD', 
    url: 'https://porkbun.com/', 
    tags: ['Promo'],
    valueScore: 95,
    priceHistory: [
      { date: '2024-01-01', price: 9.73 },
      { date: '2024-11-01', price: 7.49 } // Lowest
    ]
  },
  { id: 'com-cloudflare', tld: '.com', registrar: 'Cloudflare', registerPrice: 10.44, renewPrice: 10.44, transferPrice: 10.44, currency: 'USD', url: 'https://www.cloudflare.com/products/registrar/', tags: ['No Markup'] },
  { id: 'com-namesilo', tld: '.com', registrar: 'NameSilo', registerPrice: 8.99, renewPrice: 13.95, transferPrice: 10.95, currency: 'USD', url: 'https://www.namesilo.com/' },
  { id: 'com-aliyun', tld: '.com', registrar: 'Aliyun', registerPrice: 69, renewPrice: 85, transferPrice: 75, currency: 'CNY', url: 'https://wanwang.aliyun.com/' },
  { id: 'com-tencent', tld: '.com', registrar: 'Tencent', registerPrice: 83, renewPrice: 90, transferPrice: 80, currency: 'CNY', url: 'https://buy.cloud.tencent.com/domain' },
  { 
    id: 'com-godaddy', 
    tld: '.com', 
    registrar: 'GoDaddy', 
    registerPrice: 0.01, 
    renewPrice: 21.99, 
    transferPrice: 10.99, 
    currency: 'USD', 
    url: 'https://www.godaddy.com/', 
    tags: ['1st Year Promo'],
    valueScore: 85, // Lower score due to high renewal
    priceHistory: [
      { date: '2024-01-01', price: 0.01 },
      { date: '2024-06-01', price: 2.99 },
      { date: '2024-12-01', price: 0.01 }
    ]
  },

  // .cn
  { id: 'cn-aliyun', tld: '.cn', registrar: 'Aliyun', registerPrice: 33, renewPrice: 39, transferPrice: 35, currency: 'CNY', url: 'https://wanwang.aliyun.com/', tags: ['Best Value'] },
  { id: 'cn-tencent', tld: '.cn', registrar: 'Tencent', registerPrice: 33, renewPrice: 39, transferPrice: 33, currency: 'CNY', url: 'https://buy.cloud.tencent.com/domain' },
  { id: 'cn-huawei', tld: '.cn', registrar: 'Huawei', registerPrice: 35, renewPrice: 45, transferPrice: 35, currency: 'CNY', url: 'https://www.huaweicloud.com/' },

  // .net
  { id: 'net-spaceship', tld: '.net', registrar: 'Spaceship', registerPrice: 10.80, renewPrice: 11.98, transferPrice: 10.80, currency: 'USD', url: 'https://www.spaceship.com/' },
  { id: 'net-porkbun', tld: '.net', registrar: 'Porkbun', registerPrice: 10.90, renewPrice: 12.48, transferPrice: 12.48, currency: 'USD', url: 'https://porkbun.com/' },
  { id: 'net-aliyun', tld: '.net', registrar: 'Aliyun', registerPrice: 90, renewPrice: 95, transferPrice: 90, currency: 'CNY', url: 'https://wanwang.aliyun.com/' },
  { id: 'net-tencent', tld: '.net', registrar: 'Tencent', registerPrice: 90, renewPrice: 95, transferPrice: 90, currency: 'CNY', url: 'https://buy.cloud.tencent.com/domain' },

  // .org
  { id: 'org-spaceship', tld: '.org', registrar: 'Spaceship', registerPrice: 7.98, renewPrice: 10.98, transferPrice: 9.98, currency: 'USD', url: 'https://www.spaceship.com/' },
  { id: 'org-porkbun', tld: '.org', registrar: 'Porkbun', registerPrice: 6.80, renewPrice: 11.60, transferPrice: 11.60, currency: 'USD', url: 'https://porkbun.com/' },
  { id: 'org-aliyun', tld: '.org', registrar: 'Aliyun', registerPrice: 78, renewPrice: 85, transferPrice: 78, currency: 'CNY', url: 'https://wanwang.aliyun.com/' },

  // .io
  { id: 'io-spaceship', tld: '.io', registrar: 'Spaceship', registerPrice: 32.88, renewPrice: 39.98, transferPrice: 32.88, currency: 'USD', url: 'https://www.spaceship.com/' },
  { id: 'io-porkbun', tld: '.io', registrar: 'Porkbun', registerPrice: 34.85, renewPrice: 44.85, transferPrice: 44.85, currency: 'USD', url: 'https://porkbun.com/' },
  { id: 'io-namesilo', tld: '.io', registrar: 'NameSilo', registerPrice: 35.99, renewPrice: 46.99, transferPrice: 45.99, currency: 'USD', url: 'https://www.namesilo.com/' },

  // .ai
  { id: 'ai-spaceship', tld: '.ai', registrar: 'Spaceship', registerPrice: 68.98, renewPrice: 79.98, transferPrice: 68.98, currency: 'USD', url: 'https://www.spaceship.com/' },
  { id: 'ai-namesilo', tld: '.ai', registrar: 'NameSilo', registerPrice: 69.99, renewPrice: 89.99, transferPrice: 89.99, currency: 'USD', url: 'https://www.namesilo.com/' },
  { id: 'ai-aliyun', tld: '.ai', registrar: 'Aliyun', registerPrice: 699, renewPrice: 799, transferPrice: 699, currency: 'CNY', url: 'https://wanwang.aliyun.com/' },

  // .xyz
  { id: 'xyz-spaceship', tld: '.xyz', registrar: 'Spaceship', registerPrice: 0.67, renewPrice: 10.88, transferPrice: 9.88, currency: 'USD', url: 'https://www.spaceship.com/', tags: ['Promo'] },
  { id: 'xyz-porkbun', tld: '.xyz', registrar: 'Porkbun', registerPrice: 0.99, renewPrice: 10.38, transferPrice: 10.38, currency: 'USD', url: 'https://porkbun.com/' },
  { id: 'xyz-aliyun', tld: '.xyz', registrar: 'Aliyun', registerPrice: 8, renewPrice: 85, transferPrice: 75, currency: 'CNY', url: 'https://wanwang.aliyun.com/' },
];
